import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import dotenv from 'dotenv'
import * as urls from "../../helpers/url_helper"
import { logoutUser } from '../old/auth/login/actions'

// Debug logging helper
const log = (action, data) => {
  console.log(`[FE-DEBUG-tests.api.js] ${new Date().toISOString()} - ${action}`, data || '');
};

dotenv.config()
const url = process.env.LOCALHOST ? process.env.LOCALHOST : 'https://qa_api_gtc.amerck.com'

// Create a custom base query with error handling
const baseQuery = fetchBaseQuery({ 
  baseUrl: url, 
  timeout: 30000,
  prepareHeaders: (headers) => {
    const token = localStorage.getItem("authUser")
    if (token) {
      headers.set('auth', JSON.parse(token))
    }
    return headers
  }
})

// Add error handling wrapper
const baseQueryWithReauth = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions)
  
  if (result.error && result.error.status === 401) {
    // Dispatch logout action
    api.dispatch(logoutUser())
  }
  
  return result
}

export const testsAPI = createApi({
  reducerPath: 'testsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Tests'],
  endpoints: (builder) => ({
    // Get all test sessions with optional filters
    getTests: builder.query({
      query: (filters = {}) => ({
        url: `${urls._GET_TESTS}`,
        method: 'GET',
        params: filters,
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 10000,
      }),
      providesTags: ['Tests']
    }),

    // Get a specific test session by ID
    getTestById: builder.query({
      query: (testId) => ({
        url: `${urls._GET_TEST_BY_ID}/${testId}`,
        method: 'GET',
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 10000,
      }),
      providesTags: (result, error, id) => [{ type: 'Tests', id }]
    }),

    // Create a new test session
    runTest: builder.mutation({
      query: (testConfig) => {
        console.log('Frontend sending test config:', testConfig);
        
        // Ensure phone numbers are in the correct format 
        let phoneNumbers = [];
        
        // Handle different formats of incoming phone numbers
        if (testConfig.phoneNumbers) {
          if (Array.isArray(testConfig.phoneNumbers)) {
            // If already an array, filter out any empty values
            phoneNumbers = testConfig.phoneNumbers.filter(num => num && num.trim() !== '');
          } else if (typeof testConfig.phoneNumbers === 'string') {
            // If a single string, add it as the only element
            if (testConfig.phoneNumbers.trim() !== '') {
              phoneNumbers = [testConfig.phoneNumbers];
            }
          } else if (typeof testConfig.phoneNumbers === 'object') {
            // If an object with value property (like from a Select component)
            phoneNumbers = testConfig.phoneNumbers.map(p => 
              p.value ? p.value.trim() : (p.toString ? p.toString().trim() : '')
            ).filter(Boolean);
          }
        }
        
        // If no phone numbers are found, try to extract from the JSON string
        if (phoneNumbers.length === 0 && testConfig.rawJson) {
          try {
            const parsedJson = JSON.parse(testConfig.rawJson);
            if (parsedJson.phoneNumbers && Array.isArray(parsedJson.phoneNumbers)) {
              phoneNumbers = parsedJson.phoneNumbers.filter(Boolean);
            }
          } catch (e) {
            console.warn('Failed to parse raw JSON:', e);
          }
        }
        
        // Ensure there's at least one phone number
        if (phoneNumbers.length === 0) {
          console.error('No valid phone numbers provided in test configuration.');
          phoneNumbers = ['+1234567890']; // Add a default for testing only
        }
        
        // Get phone numbers from the array
        const phoneNumber1 = phoneNumbers.length > 0 ? phoneNumbers[0] : "";
        const phoneNumber2 = phoneNumbers.length > 1 ? phoneNumbers[1] : "";
        
        // Ensure we have a valid name to avoid null name errors
        const testName = testConfig.name || "Unnamed Test";
        
        // Format data for Telq API with both formats for compatibility
        const telqConfig = {
          // Put name at the root level for the service
          name: testName,
          config: {
            // Include name in config object as well for flexibility
            name: testName,
            // Include both formats for phone numbers
            phoneNumbers: phoneNumbers,
            phoneNumberObjects: testConfig.phoneNumberObjects,
            msisdn1: phoneNumber1,
            msisdn2: phoneNumber2,
            
            // Include both sender and customer field
            // Prefer the sender from the DB (via template option), fall back to templateLabel
            sender: testConfig.template?.sender || testConfig.templateLabel || testConfig.template?.label || "Missing Template Label",
            customer: testConfig.customer?.label || "Test Customer",
            customerID: testConfig.customer?.value || null,
            
            // Message details
            text: testConfig.messageText?.value || testConfig.messageText || "",  // Old field name
            messageText: testConfig.messageText?.value || testConfig.messageText || "", // New field name expected by backend
            
            code: testConfig.format || "NUMERIC", // Old field name
            format: testConfig.format || "NUMERIC", // New field name expected by backend
            
            type: testConfig.type || "MIXED", // Field expected by backend
            code_case: testConfig.type || "MIXED", // Old field name
            
            length: testConfig.codeLength || 6, // Old field name
            codeLength: testConfig.codeLength || 6, // New field name expected by backend
            
            snooze: testConfig.delayTime || 2.5, // Use delayTime from form config, default to 2.5
            
            // Include template info if available
            template: testConfig.template?.value || testConfig.template || "",
            templateLabel: testConfig.templateLabel || testConfig.template?.label || "Missing Template Label",
            language: testConfig.language?.value || testConfig.language || "",
            
            // Include operator info
            operator: testConfig.operator?.label || testConfig.operator?.value || testConfig.operator || "",
            
            // Routes information with both formats
            routes: testConfig.routes || [],
            routeselection1: testConfig.routes?.[0]?.routeType 
              ? (typeof testConfig.routes[0].routeType === 'object' && testConfig.routes[0].routeType.name 
                ? testConfig.routes[0].routeType.name 
                : testConfig.routes[0].routeType) 
              : testConfig.routes?.[0]?.value || "active LM",
            routeselection2: testConfig.routes?.[1]?.routeType 
              ? (typeof testConfig.routes[1].routeType === 'object' && testConfig.routes[1].routeType.name 
                ? testConfig.routes[1].routeType.name 
                : testConfig.routes[1].routeType) 
              : testConfig.routes?.[1]?.value || "active IB",
            
            // Include custom supplier ID if available
            customSupplierId: testConfig.customSupplierId !== undefined && testConfig.customSupplierId !== null 
              ? testConfig.customSupplierId 
              : null
          }
        };
        
        // console.log('[FRONTEND - tests.api.js] Sending test configuration to backend:', telqConfig);
        // console.log('[FRONTEND - tests.api.js] Delay time being sent as snooze:', telqConfig.config.snooze);
        // console.log('[FRONTEND - tests.api.js] Original delayTime from testConfig:', testConfig.delayTime);
        
        return {
          url: `${urls._RUN_TEST}`,
          method: 'POST',
          body: telqConfig,
          headers: {
            auth: JSON.parse(localStorage.getItem("authUser")),
          },
          timeout: 30000,
        };
      },
      transformResponse: (response) => {
        console.log('Run test response:', response);
        if (response.success) {
          return response;
        }
        throw new Error(response.message || 'Unknown error occurred');
      },
      transformErrorResponse: (error) => {
        console.error('Run test error:', error);
        return { 
          status: error.status,
          data: {
            success: false,
            message: error.data?.message || 'Failed to create test session'
          }
        };
      },
      invalidatesTags: ['Tests']
    }),

    // Stop a running test
    cancelTest: builder.mutation({
      query: (testId) => ({
        url: `${urls._GET_TEST_BY_ID}/${testId}/stop`,
        method: 'POST',
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        }
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Tests', id }]
    }),

    // Pause a running test
    pauseTest: builder.mutation({
      query: (testId) => {
        log('PAUSE_TEST_API_REQUEST', { testId });
        return {
          url: `${urls._GET_TEST_BY_ID}/${testId}/pause`,
          method: 'POST',
          headers: {
            auth: JSON.parse(localStorage.getItem("authUser")),
          },
          timeout: 15000,
        };
      },
      transformResponse: (response) => {
        log('PAUSE_TEST_API_RESPONSE', { status: response.status, data: response.data });
        return response;
      },
      transformErrorResponse: (error) => {
        log('PAUSE_TEST_API_ERROR', { status: error.status, message: error.data?.message });
        return error;
      },
      invalidatesTags: (result, error, id) => [{ type: 'Tests', id }]
    }),

    // Get test results
    getTestResults: builder.query({
      query: (testId) => ({
        url: `${urls._GET_TEST_BY_ID}/${testId}/results`,
        method: 'GET',
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 15000,
      })
    }),

    // Get test status
    getTestStatus: builder.query({
      query: (testId) => ({
        url: `${urls._GET_TEST_BY_ID}/${testId}/status`,
        method: 'GET',
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 10000,
      })
    }),

    // Start a test session
    startTest: builder.mutation({
      query: (testId) => {
        log('START_TEST_API_REQUEST', { testId });
        return {
          url: `${urls._GET_TEST_BY_ID}/${testId}/start`,
          method: 'POST',
          headers: {
            auth: JSON.parse(localStorage.getItem("authUser")),
          },
          timeout: 15000,
        };
      },
      transformResponse: (response) => {
        log('START_TEST_API_RESPONSE', { status: response.status, data: response.data });
        return response;
      },
      transformErrorResponse: (error) => {
        log('START_TEST_API_ERROR', { status: error.status, message: error.data?.message });
        return error;
      },
      invalidatesTags: ['Tests']
    }),

    // Get test templates
    getTestTemplates: builder.query({
      query: () => ({
        url: `${urls._GET_TEST_TEMPLATES}`,
        method: 'GET',
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        }
      })
    }),

    // Delete a test session
    deleteTest: builder.mutation({
      query: (testId) => ({
        url: `${urls._GET_TESTS}/deleteSession`,
        method: 'POST',
        body: { id: testId },
        headers: {
          auth: JSON.parse(localStorage.getItem("authUser")),
        },
        timeout: 10000,
      }),
      transformResponse: (response) => {
        if (response && response.success) {
          return { success: true, message: response.message || 'Test deleted successfully' };
        }
        return { success: false, message: response.message || 'Unknown error occurred' };
      },
      transformErrorResponse: (error) => {
        console.error('Delete test error:', error);
        return { 
          status: error.status, 
          data: { 
            message: error.data?.message || 'Failed to delete test' 
          } 
        };
      },
      invalidatesTags: ['Tests']
    }),

    // Create a test session directly from JSON
    createTestFromJson: builder.mutation({
      query: (jsonData) => {
        console.log('Creating test from raw JSON:', typeof jsonData === 'string' ? jsonData : JSON.stringify(jsonData));
        
        return {
          url: `${urls._RUN_TEST}/from-json`,
          method: 'POST',
          body: { 
            jsonData: typeof jsonData === 'string' ? jsonData : JSON.stringify(jsonData)
          },
          headers: {
            auth: JSON.parse(localStorage.getItem("authUser")),
          },
          timeout: 30000,
        };
      },
      transformResponse: (response) => {
        console.log('Create from JSON response:', response);
        if (response.success) {
          return response;
        }
        throw new Error(response.message || 'Unknown error occurred');
      },
      transformErrorResponse: (error) => {
        console.error('Create from JSON error:', error);
        return { 
          status: error.status,
          data: {
            success: false,
            message: error.data?.message || 'Failed to create test session from JSON'
          }
        };
      },
      invalidatesTags: ['Tests']
    }),
  }),
})

export const {
  useGetTestsQuery,
  useLazyGetTestsQuery,
  useGetTestByIdQuery,
  useLazyGetTestByIdQuery,
  useRunTestMutation,
  useCancelTestMutation,
  usePauseTestMutation,
  useGetTestResultsQuery,
  useLazyGetTestResultsQuery,
  useGetTestStatusQuery,
  useLazyGetTestStatusQuery,
  useStartTestMutation,
  useGetTestTemplatesQuery,
  useDeleteTestMutation,
  useCreateTestFromJsonMutation
} = testsAPI 